import { ReactElement, useMemo, useCallback } from 'react';
import { Stack, Typography, Avatar, Box } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTruckFast as VehicleIcon,
  faVectorSquare as LogisticsZonesIcon,
  faDollarSign as PriceIcon,
} from '@fortawesome/free-solid-svg-icons';
import { compact } from 'lodash';

import { OrderActiveFilters } from '~components/index';
import { OrderActiveFiltersItem } from '~components/OrderActiveFilters/types';
import { Nullable } from '~globals/types/commons';
import { formatterDate, formatCurrencyValue } from '~utils/formatter';
import { getDefaultAvatar } from '~utils/user';

// eslint-disable-next-line max-len
import { useDashboardVariantV2CarrierFinalizedFiltersContext } from '../../../../../FinalizedFiltersContext';
import { useDashboardVariantV2CarrierPresetsContext } from '../../../../../PresetsContext';
import { useDashboardVariantV2CarrierContentFinalizedTabContext } from '../../Context';

import { DashboardVariantV2CarrierContentActionsContainer } from '../../../../styles';

const DashboardVariantV2CarrierContentFinalizedTabCurrenFilters =
  (): Nullable<ReactElement> => {
    const {
      autocompletesRefs: {
        vehicles: vehiclesAutocompleteRef,
        drivers: driversAutocompleteRef,
        services: servicesAutocompleteRef,
        logisticsZones: logisticsZonesAutocompleteRef,
        assignedUser: assignedUserAutocompleteRef,
        sourceImports: sourceImportsAutocompleteRef,
        itemState: itemStateAutocompleteRef,
      },
      filters: {
        filtersActives,
        handleClearFilters,
        handleClearFilterByKey,
        handleClearFilterByValues,
      },
    } = useDashboardVariantV2CarrierFinalizedFiltersContext();

    const {
      selectedPreset: { reset: handleResetSelectedPreset },
    } = useDashboardVariantV2CarrierPresetsContext();

    const {
      actions: { handleForceRequests },
    } = useDashboardVariantV2CarrierContentFinalizedTabContext();

    const onClearFilter = useCallback(() => {
      handleClearFilters(true);
      handleResetSelectedPreset();
    }, [handleClearFilters, handleResetSelectedPreset]);

    const activeFilters = useMemo<OrderActiveFiltersItem[]>(() => {
      const currentFilters: OrderActiveFiltersItem[] = [];

      if (filtersActives.search) {
        currentFilters.push({
          id: 'search',
          label: filtersActives.search,
          tooltip: 'Nro. Orden / Nro. Tracking / Grupo',
          onClear: () =>
            handleClearFilterByKey('search', '', handleForceRequests),
        });
      }

      const price = compact([filtersActives.priceGt, filtersActives.priceLt]);

      if (price.length > 0) {
        const priceLabel = price
          .map((currentPrice) =>
            formatCurrencyValue(Number(currentPrice), { style: 'decimal' }),
          )
          .join(' - ');

        currentFilters.push({
          id: 'price',
          label: (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <FontAwesomeIcon
                icon={PriceIcon}
                style={{ width: 15, height: 15 }}
              />

              <Typography variant="inherit">{priceLabel}</Typography>
            </Stack>
          ),
          tooltip: 'Precio',
          onClear: () =>
            handleClearFilterByValues(
              { priceGt: '', priceLt: '' },
              handleForceRequests,
            ),
        });
      }

      const rangeFinalizedDates = compact([
        filtersActives.finalizedDateTimeFrom,
        filtersActives.finalizedDateTimeTo,
      ]);

      if (rangeFinalizedDates.length > 0) {
        const rangeFinalizedDatesLabel = rangeFinalizedDates
          .map((date) =>
            formatterDate(date, {
              format: 'DD/MM/YYYY',
            }),
          )
          .join(' - ');

        currentFilters.push({
          id: 'rangeFinalizedDates',
          label: rangeFinalizedDatesLabel,
          tooltip: 'Rango de fechas de finalización',
          onClear: () =>
            handleClearFilterByValues(
              { finalizedDateTimeFrom: null, finalizedDateTimeTo: null },
              handleForceRequests,
            ),
        });
      }

      const rangeExpirationDates = compact([
        filtersActives.expirationDateTimeFrom,
        filtersActives.expirationDateTimeTo,
      ]);

      if (rangeExpirationDates.length > 0) {
        const rangeExpirationDatesLabel = rangeExpirationDates
          .map((date) =>
            formatterDate(date, {
              format: 'DD/MM/YYYY',
            }),
          )
          .join(' - ');

        currentFilters.push({
          id: 'rangeExpirationDates',
          label: rangeExpirationDatesLabel,
          tooltip: 'Rango de fechas de vencimiento',
          onClear: () =>
            handleClearFilterByValues(
              { expirationDateTimeFrom: null, expirationDateTimeTo: null },
              handleForceRequests,
            ),
        });
      }

      if (filtersActives.includeWithoutExpirationDate) {
        currentFilters.push({
          id: 'includeWithoutExpirationDate',
          label: 'Sin fecha de vencimiento',
          tooltip: 'Incluir pedidos sin fecha de vencimiento',
          onClear: () =>
            handleClearFilterByKey(
              'includeWithoutExpirationDate',
              null,
              handleForceRequests,
            ),
        });
      }

      const rangeCreationDates = compact([
        filtersActives.dateFrom,
        filtersActives.dateTo,
      ]);

      if (rangeCreationDates.length > 0) {
        const rangeCreationDatesLabel = rangeCreationDates
          .map((date) =>
            formatterDate(date, {
              format: 'DD/MM/YYYY',
            }),
          )
          .join(' - ');

        currentFilters.push({
          id: 'rangeCreationDates',
          label: rangeCreationDatesLabel,
          tooltip: 'Rango de fechas de creación',
          onClear: () =>
            handleClearFilterByValues(
              { dateFrom: null, dateTo: null },
              handleForceRequests,
            ),
        });
      }

      if (
        filtersActives.itemState !== null &&
        filtersActives.itemState.length > 0
      ) {
        const itemStateLabels = filtersActives.itemState
          .map((service) => service.label.toString())
          .join(' / ');

        currentFilters.push({
          id: 'itemState',
          label: itemStateLabels,
          tooltip: 'Estados',
          onClear: () => {
            itemStateAutocompleteRef.current?.resetAutocomplete();
            handleClearFilterByKey('itemState', null, handleForceRequests);
          },
        });
      }

      if (
        filtersActives.vehicles !== null &&
        filtersActives.vehicles.length > 0
      ) {
        const vehiclesCount = filtersActives.vehicles.length - 1;

        const vehiclesLabel = filtersActives.vehicles.map(
          (vehicle, vehicleIndex) => (
            <Stack
              key={`filters-active-vehicle-${vehicle.id}`}
              direction="row"
              spacing={0.5}
              alignItems="center"
            >
              <FontAwesomeIcon
                icon={VehicleIcon}
                style={{ color: vehicle.color, width: 15, height: 15 }}
              />

              <Typography variant="inherit">{vehicle.displayName}</Typography>

              {vehicleIndex < vehiclesCount && (
                <Typography variant="inherit">/</Typography>
              )}
            </Stack>
          ),
        );

        currentFilters.push({
          id: 'vehicles',
          label: vehiclesLabel,
          tooltip: 'Vehículos',
          onClear: () => {
            vehiclesAutocompleteRef.current?.resetAutocomplete();
            handleClearFilterByKey('vehicles', null, handleForceRequests);
          },
        });
      }

      if (
        filtersActives.drivers !== null &&
        filtersActives.drivers.length > 0
      ) {
        const driversCount = filtersActives.drivers.length - 1;

        const driversLabel = filtersActives.drivers.map(
          (driver, driverIndex) => (
            <Stack
              key={`filters-active-driver-${driver.id}`}
              direction="row"
              spacing={0.5}
              alignItems="center"
            >
              <Avatar
                src={getDefaultAvatar(driver.avatar ?? undefined)}
                alt={driver.displayName}
                sx={{ width: 15, height: 15 }}
              />

              <Typography variant="inherit">{driver.displayName}</Typography>

              {driverIndex < driversCount && (
                <Typography variant="inherit">/</Typography>
              )}
            </Stack>
          ),
        );

        currentFilters.push({
          id: 'drivers',
          label: driversLabel,
          tooltip: 'Choferes',
          onClear: () => {
            driversAutocompleteRef.current?.resetAutocomplete();
            handleClearFilterByKey('drivers', null, handleForceRequests);
          },
        });
      }

      if (
        filtersActives.services !== null &&
        filtersActives.services.length > 0
      ) {
        const servicesLabel = filtersActives.services
          .map((service) => service.label.toString())
          .join(' / ');

        currentFilters.push({
          id: 'services',
          label: servicesLabel,
          tooltip: 'Tipos de servicios',
          onClear: () => {
            servicesAutocompleteRef.current?.resetAutocomplete();
            handleClearFilterByKey('services', null, handleForceRequests);
          },
        });
      }

      if (filtersActives.zones !== null && filtersActives.zones.length > 0) {
        const zonesCount = filtersActives.zones.length - 1;

        const zonesLabel = filtersActives.zones.map((zone, zoneIndex) => (
          <Stack
            key={`filters-active-zone-${zone.zoneId}`}
            direction="row"
            spacing={0.5}
            alignItems="center"
          >
            {zone.color && (
              <FontAwesomeIcon
                icon={LogisticsZonesIcon}
                style={{ color: zone.color, width: 15, height: 15 }}
              />
            )}

            <Typography variant="inherit">{zone.code}</Typography>

            {zoneIndex < zonesCount && (
              <Typography variant="inherit">/</Typography>
            )}
          </Stack>
        ));

        currentFilters.push({
          id: 'zones',
          label: zonesLabel,
          tooltip: 'Zonas logísticas',
          onClear: () => {
            logisticsZonesAutocompleteRef.current?.resetAutocomplete();
            handleClearFilterByKey('zones', null, handleForceRequests);
          },
        });
      }

      if (filtersActives.assignedUser) {
        // eslint-disable-next-line max-len
        const assignedUserName = `${filtersActives.assignedUser.lastName}, ${filtersActives.assignedUser.firstName}`;

        currentFilters.push({
          id: 'assignedUser',
          label: (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Avatar
                src={getDefaultAvatar(
                  filtersActives.assignedUser.avatarUrl || undefined,
                )}
                alt={assignedUserName}
                sx={{ width: 15, height: 15 }}
              />

              <Typography variant="inherit">{assignedUserName}</Typography>
            </Stack>
          ),
          tooltip: 'Usuario asignado',
          onClear: () => {
            assignedUserAutocompleteRef.current?.resetAutocomplete();
            handleClearFilterByKey('assignedUser', null, handleForceRequests);
          },
        });
      }

      if (
        filtersActives.sourceImports !== null &&
        filtersActives.sourceImports.length > 0
      ) {
        const sourceImportsCount = filtersActives.sourceImports.length - 1;

        const sourceImportsLabel = filtersActives.sourceImports.map(
          (sourceImport, sourceImportIndex) => (
            <Stack
              key={`filters-active-source-import-${sourceImport.value}`}
              direction="row"
              spacing={0.5}
              alignItems="center"
            >
              <Box
                component="img"
                src={sourceImport.image.url}
                alt={sourceImport.image.alt}
                sx={{ height: 15, width: 15, objectFit: 'contain' }}
              />

              <Typography variant="inherit">{sourceImport.label}</Typography>

              {sourceImportIndex < sourceImportsCount && (
                <Typography variant="inherit">/</Typography>
              )}
            </Stack>
          ),
        );

        currentFilters.push({
          id: 'sourceImports',
          label: sourceImportsLabel,
          tooltip: 'Medios de importación',
          onClear: () => {
            sourceImportsAutocompleteRef.current?.resetAutocomplete();
            handleClearFilterByKey('sourceImports', null, handleForceRequests);
          },
        });
      }

      return currentFilters;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      filtersActives,
      handleClearFilterByKey,
      handleClearFilterByValues,
      handleForceRequests,
    ]);

    if (activeFilters.length === 0) return null;

    return (
      <DashboardVariantV2CarrierContentActionsContainer>
        <OrderActiveFilters
          filters={activeFilters}
          onClearFilters={onClearFilter}
        />
      </DashboardVariantV2CarrierContentActionsContainer>
    );
  };

export default DashboardVariantV2CarrierContentFinalizedTabCurrenFilters;
